import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { PermissionsEnum } from '../utils/auth.types';
import { IRole } from '../utils/user.types';

/**
 * Hook to check user permissions
 */
export const usePermissions = () => {
  const permissions = useSelector((state: RootState) => state.auth.permissions);
  const user = useSelector((state: RootState) => state.auth.user);

  // Debug logging (remove in production)
  if (process.env.NODE_ENV === 'development') {
    console.log('usePermissions - permissions count:', permissions?.length || 0);
    console.log('usePermissions - user role:', user?.role?.[0]?.role);
  }

  /**
   * Check if user has a specific permission
   */
  const hasPermission = (permission: PermissionsEnum | string): boolean => {
    if (!permissions || !Array.isArray(permissions)) {
      return false;
    }

    // Use the new module permission system - remove superAdmin bypass
    // Permissions are now managed through UserModuleAccess system
    
    // Convert enum to backend format
    const backendPermission = convertToBackendFormat(permission);
    return permissions.includes(backendPermission);
  };

  /**
   * Check if user has any of the specified permissions
   */
  const hasAnyPermission = (permissionList: (PermissionsEnum | string)[]): boolean => {
    return permissionList.some(permission => hasPermission(permission));
  };

  /**
   * Check if user has all of the specified permissions
   */
  const hasAllPermissions = (permissionList: (PermissionsEnum | string)[]): boolean => {
    return permissionList.every(permission => hasPermission(permission));
  };

  /**
   * Get all user permissions
   */
  const getAllPermissions = (): string[] => {
    return permissions || [];
  };

  /**
   * Check if user is super admin
   */
  const isSuperAdmin = (): boolean => {
    return user?.role?.some((role: IRole) => role.role === 'superAdmin') || false;
  };

  /**
   * Check if user is admin (includes super admin)
   */
  const isAdmin = (): boolean => {
    return user?.role?.some((role: IRole) => ['superAdmin', 'admin'].includes(role.role)) || false;
  };

  /**
   * Get user role
   */
  const getUserRole = (): string => {
    return user?.role?.[0]?.role || 'member';
  };

  /**
   * Convert frontend permission enum to backend format
   */
  const convertToBackendFormat = (permission: PermissionsEnum | string): string => {
    if (typeof permission === 'string' && permission.includes(':')) {
      return permission; // Already in backend format
    }

    // Convert enum format to backend format
    const enumString = permission.toString();
    const parts = enumString.split('_');
    if (parts.length >= 2) {
      const resource = parts[0].toLowerCase();
      const action = parts[1].toLowerCase();
      return `${action}:${resource}`;
    }

    return enumString.toLowerCase();
  };

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getAllPermissions,
    isSuperAdmin,
    isAdmin,
    getUserRole,
    permissions
  };
};

/**
 * Helper hook for specific permission checks
 */
export const useSpecificPermissions = () => {
  const { hasPermission } = usePermissions();

  return {
    // User Management
    canCreateUser: () => hasPermission(PermissionsEnum.Users_Create),
    canViewUser: () => hasPermission(PermissionsEnum.Users_View),
    canUpdateUser: () => hasPermission(PermissionsEnum.Users_Update),
    canDeleteUser: () => hasPermission(PermissionsEnum.Users_Delete),

    // // Invoice Management
    // canCreateInvoice: () => hasPermission(PermissionsEnum.Invoice_Create),
    // canViewInvoice: () => hasPermission(PermissionsEnum.Invoice_View),
    // canUpdateInvoice: () => hasPermission(PermissionsEnum.Invoice_Update),
    // canDeleteInvoice: () => hasPermission(PermissionsEnum.Invoice_Delete),
    // canSendInvoice: () => hasPermission(PermissionsEnum.Invoice_Send),

    // // Customer Management
    // canCreateCustomer: () => hasPermission(PermissionsEnum.Customer_Create),
    // canViewCustomer: () => hasPermission(PermissionsEnum.Customer_View),
    // canUpdateCustomer: () => hasPermission(PermissionsEnum.Customer_Update),
    // canDeleteCustomer: () => hasPermission(PermissionsEnum.Customer_Delete),

    // // Supplier Management
    // canCreateSupplier: () => hasPermission(PermissionsEnum.Supplier_Create),
    // canViewSupplier: () => hasPermission(PermissionsEnum.Supplier_View),
    // canUpdateSupplier: () => hasPermission(PermissionsEnum.Supplier_Update),
    // canDeleteSupplier: () => hasPermission(PermissionsEnum.Supplier_Delete),

    // // Employee Management
    // canCreateEmployee: () => hasPermission(PermissionsEnum.Employee_Create),
    // canViewEmployee: () => hasPermission(PermissionsEnum.Employee_View),
    // canUpdateEmployee: () => hasPermission(PermissionsEnum.Employee_Update),
    // canDeleteEmployee: () => hasPermission(PermissionsEnum.Employee_Delete),

    // // Payment Management
    // canCreatePayment: () => hasPermission(PermissionsEnum.Payment_Create),
    // canViewPayment: () => hasPermission(PermissionsEnum.Payment_View),
    // canUpdatePayment: () => hasPermission(PermissionsEnum.Payment_Update),
    // canDeletePayment: () => hasPermission(PermissionsEnum.Payment_Delete),

    // // Reports & Analytics
    // canViewReports: () => hasPermission(PermissionsEnum.Reports_View),
    // canExportReports: () => hasPermission(PermissionsEnum.Reports_Export),
    // canViewDashboard: () => hasPermission(PermissionsEnum.Dashboard_View),

    // // Settings
    // canViewSettings: () => hasPermission(PermissionsEnum.Settings_View),
    // canUpdateSettings: () => hasPermission(PermissionsEnum.Settings_Update),

    // // Cashflow
    // canViewCashflow: () => hasPermission(PermissionsEnum.Cashflow_View),
    // canManageCashflow: () => hasPermission(PermissionsEnum.Cashflow_Manage)
  };
};